import { useEffect, useRef, useState } from 'react'
import TextField, { type TextFieldProps } from '@mui/material/TextField'
import { nextDigits, timeDigitsFromValue, timeFromDigits } from '@jupi/shared'

// A clock time typed as digits: "1000" becomes "10:00", the way quick time
// entry does for the flight's four times, on a desktop keyboard as on a
// phone. The value is "HH:MM", or '' until what is typed is a real time.
export function TimeField({
  value,
  onChange,
  ...props
}: Omit<TextFieldProps, 'value' | 'onChange' | 'type'> & { value: string; onChange: (value: string) => void }) {
  const [digits, setDigits] = useState(() => value.replace(':', ''))
  const shown = timeFromDigits(digits)

  // Follow the value when it is set from outside (a booking fills the times).
  // What the field itself just reported is not that: an impossible time is
  // reported as '' and must stay on screen, flagged, not be cleared.
  const reported = useRef<string | null>(null)
  useEffect(() => {
    const next = timeDigitsFromValue(value, reported.current)
    if (next === null) return
    reported.current = value
    setDigits(next)
  }, [value])

  const invalid = shown.complete && !shown.time
  return (
    <TextField
      {...props}
      placeholder="HH:MM"
      value={shown.text}
      error={props.error || invalid}
      onChange={(e) => {
        const next = nextDigits(digits, shown.text, e.target.value, 4)
        setDigits(next)
        const time = timeFromDigits(next).time ?? ''
        reported.current = time
        onChange(time)
      }}
      slotProps={{
        ...props.slotProps,
        inputLabel: { shrink: true },
        htmlInput: { ...(props.slotProps?.htmlInput as object), inputMode: 'numeric', autoComplete: 'off' },
      }}
    />
  )
}
